export const EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS_VERSION = 2;

export const EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS = [
  { key: "arrivalPhoto", labelKey: "ecr_arrival_photo", label: "Arrival Photo", required: true, arrival: true },
  { key: "containerFrontDoorPhoto", labelKey: "container_front_door_photo", label: "Container Front Door", required: true },
  { key: "containerBackDoorPhoto", labelKey: "container_back_door_photo", label: "Container Back Door", required: true },
  { key: "containerFrontPhoto", labelKey: "container_front_photo", label: "Container Front", required: true },
  { key: "containerOutsideLeftPhoto", labelKey: "container_outside_left_photo", label: "Container Outside Left Side", required: true },
  { key: "containerOutsideRightPhoto", labelKey: "container_outside_right_photo", label: "Container Outside Right Side", required: true },
  { key: "containerInsideLeftPhoto", labelKey: "container_inside_left_photo", label: "Container Inside Left Side", required: true },
  { key: "containerInsideRightPhoto", labelKey: "container_inside_right_photo", label: "Container Inside Right Side", required: true },
  { key: "containerBackPhoto", labelKey: "container_back_photo", label: "Container Back", required: true },
  { key: "yardGatePhoto", labelKey: "yard_gate_photo", label: "Yard Gate", required: true },
  { key: "damagePhoto", labelKey: "damage_photo", label: "Damage Photo", required: false, damageConditional: true }
];

export const EMPTY_CONTAINER_RETURN_UPLOAD_REQUIREMENTS = EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS.filter(
  (requirement) => !requirement.arrival
);

export const isEmptyContainerReturnPhotoRequired = (requirement, status, rejectionReason) => {
  if (!requirement) return false;
  if (requirement.required) return true;
  if (!requirement.damageConditional) return false;
  return String(status || "").trim().toLowerCase() === "rejected"
    || String(rejectionReason || "").trim().toLowerCase() === "container damaged";
};

export const getMissingEmptyContainerReturnPhotos = (photos, status, rejectionReason) => (
  EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS
    .filter((requirement) => isEmptyContainerReturnPhotoRequired(requirement, status, rejectionReason))
    .filter((requirement) => !(photos && photos[requirement.key]))
);

const getPhotoUrl = (photo) => {
  if (typeof photo === "string") return photo;
  return photo ? (photo.url || photo.imageUrl || photo.downloadURL || "") : "";
};

export const getEmptyContainerReturnPhotoUrl = (record = {}, key) => {
  if (key === "arrivalPhoto") {
    return record.arrivalPictureUrl || record.gpsArrivalPictureUrl
      || getPhotoUrl(record.arrivalPictureProof) || "";
  }
  return getPhotoUrl(record.proofs && record.proofs[key]) || getPhotoUrl(record[key]);
};

const LEGACY_PHOTOS = [
  EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS[0],
  { key: "containerFrontPhoto", labelKey: "legacy_container_front_photo", label: "Container Front Photo", required: true },
  { key: "containerSidePhoto", labelKey: "legacy_container_side_photo", label: "Container Side Photo", required: true },
  EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS.find((item) => item.key === "yardGatePhoto"),
  EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS.find((item) => item.key === "damagePhoto")
];

// Historical returns retain the checklist they were submitted against. A generic
// old side photo must never be relabelled as one of the new, specific angles.
export const getEmptyContainerReturnDisplayPhotos = (record = {}) => {
  const requirements = Number(record.photoRequirementsVersion) >= 2
    ? EMPTY_CONTAINER_RETURN_PHOTO_REQUIREMENTS
    : LEGACY_PHOTOS;
  return requirements.map((item) => ({
    ...item,
    required: isEmptyContainerReturnPhotoRequired(item, record.status || record.emptyReturnStatus,
      record.emptyReturnRejectedReason || record.rejectionReason),
    url: getEmptyContainerReturnPhotoUrl(record, item.key),
    proof: item.arrival ? record.arrivalPictureProof : (record.proofs && record.proofs[item.key])
  }));
};

// Run before allocating a return ID, reading settings, or uploading any file.
export const validateEmptyContainerReturnSubmission = (returnData = {}, arrivalFile) => {
  const { status, proofFiles = {}, eirReceiptFile } = returnData;
  if (status !== "Accepted" && status !== "Rejected") throw new Error("Select Accepted or Rejected.");
  const rejectionReason = returnData.emptyReturnRejectedReason || returnData.rejectionReason || "";
  const missing = getMissingEmptyContainerReturnPhotos({ ...proofFiles, arrivalPhoto: arrivalFile }, status, rejectionReason);
  if (missing.length) throw new Error(`Required proof photos missing: ${missing.map((item) => item.label).join(', ')}.`);
  if (!eirReceiptFile) throw new Error("EIR / Yard Receipt is required.");
  if (status === "Rejected" && !String(rejectionReason).trim()) throw new Error("Container Yard Reason of Rejection is required for rejected empty container returns.");
  const files = [
    { label: "Arrival Photo", file: arrivalFile, types: ["image/jpeg", "image/png"] },
    ...EMPTY_CONTAINER_RETURN_UPLOAD_REQUIREMENTS.map((item) => ({
      label: item.label, file: proofFiles[item.key], types: ["image/jpeg", "image/png"]
    })),
    { label: "EIR / Yard Receipt", file: eirReceiptFile, types: ["image/jpeg", "image/png", "application/pdf"] }
  ];
  files.filter((item) => item.file).forEach(({ label, file, types }) => {
    if (typeof file.size !== "number" || file.size <= 0) throw new Error(`${label}: select a non-empty file.`);
    if (file.type && !types.includes(file.type)) throw new Error(`${label}: unsupported file type.`);
  });
};
